import React, { useEffect, useRef, useState } from 'react';
import * as Contentful from 'contentful';
import { ICustomer, ValidatedState, checkEmailDomain, checkValidation, getHelperText } from '@adac-mp/shared';
import { HelperTextWithIcon, Input, MailIcon } from '../../index';
import { useValidatedStates } from '../../hooks/useValidatedStates';

type TypeEmailLabels = {
	email?: Contentful.EntryFields.Symbol;
	emailError?: Contentful.EntryFields.Symbol;
	emailRepeat?: Contentful.EntryFields.Symbol;
	emailRepeatError?: Contentful.EntryFields.Symbol;
};
interface IEmailInputProps {
	readonly?: boolean;
	labels: TypeEmailLabels;
	validateFields?: boolean;
	state: ValidatedState;
	customer?: ICustomer;
	updateEmail: (val: string, validationState?: { error?: boolean; confirmed?: boolean }) => void;
}

export const EmailInput = ({ state: emailState, labels, customer, readonly, updateEmail }: IEmailInputProps) => {
	const emailInputRef = useRef<HTMLInputElement | null>(null);
	const confirmationEmailInputRef = useRef<HTMLInputElement | null>(null);
	const [state, { update }] = useValidatedStates({ confirmationEmail: { value: '', error: false, required: true } });
	const [isLoading, setIsLoading] = useState(false);
	const [domainChecked, setDomainChecked] = useState(false);
	const [emailConfirmed, setEmailConfirmed] = useState(false);
	const [editingEmail, setEditingEmail] = useState(true);
	const [validateConfirmationEmail, setValidateConfirmationEmail] = useState(false);

	const emailCorrectlyEntered = emailState.value && emailState.value.length > 0 && !emailState.error;

	const handleEmailValidation = (val: string) => {
		setEditingEmail(false);
		if (val && domainChecked) return;
		if (!checkValidation(val, 'EMAIL')) {
			setIsLoading(true);
			checkEmailDomain(val).then(({ errors }) => {
				setIsLoading(false);
				setDomainChecked(true);
				const error = errors.length > 0;
				updateEmail(val, { error });
			});
		} else {
			updateEmail(val, { error: true });
		}
	};

	const renderErrorMessage = () => {
		if (emailState.error) return labels?.emailError ?? getHelperText('EMAIL');
		return (
			labels.emailRepeatError ?? 'Die Email Adressen stimmen nicht überein. Bitte überprüfen Sie Ihre Eingabe.'
		);
	};

	const confirmationInputIsDisabled = () => !(domainChecked && !emailState.error);

	const emailInputsAreInvalid = () =>
		emailState.error ||
		(domainChecked && validateConfirmationEmail && !state.confirmationEmail.error && !emailConfirmed);

	useEffect(() => {
		if (domainChecked && !emailState.error) {
			confirmationEmailInputRef.current?.focus();
		}
	}, [domainChecked, emailState.error]);

	useEffect(() => {
		if (emailState.value && !emailState.error) {
			setDomainChecked(true);
		}
	}, []);

	return (
		<>
			<Input
				ref={emailInputRef}
				placeholder={labels?.email || 'Email'}
				mandatory={emailState.required}
				icon={<MailIcon />}
				type={emailCorrectlyEntered && !emailConfirmed && !editingEmail ? 'password' : 'email'}
				loading={isLoading}
				value={
					readonly && customer?.communications
						? customer.communications[0].details
						: (emailState.value as string | undefined)
				}
				readonly={readonly}
				hasError={emailInputsAreInvalid()}
				name="email"
				autocomplete="email"
				onFocus={() => {
					setEditingEmail(true);
				}}
				onBlur={(e) => {
					if (!readonly) handleEmailValidation(e.target.value);
				}}
				onChange={(e) => {
					const autofill =
						emailInputRef.current?.matches(':-webkit-autofill') ||
						emailInputRef.current?.matches(':autofill');
					if (autofill) {
						handleEmailValidation(e.target.value);
						return;
					}
					updateEmail(e.target.value);
					setDomainChecked(false);
					if (state.confirmationEmail.value) {
						update('confirmationEmail', '');
					}
					if (validateConfirmationEmail) {
						setValidateConfirmationEmail(false);
					}
				}}
			/>
			{!readonly && (
				<>
					<Input
						ref={confirmationEmailInputRef}
						autocomplete="new-email"
						placeholder={labels.emailRepeat || 'Wiederholung der Email'}
						mandatory={state.confirmationEmail.required}
						icon={<MailIcon stroke={confirmationInputIsDisabled() ? '#A3A3A3' : ''} />}
						type="email"
						readonly={readonly}
						disabled={confirmationInputIsDisabled()}
						value={state.confirmationEmail.value}
						hasError={!emailConfirmed}
						name="email-confirmation"
						onFocus={(e) => {
							const confirmed = e.target.value === emailState.value;
							setEmailConfirmed(confirmed);
						}}
						onChange={(e) => {
							setEditingEmail(false);
							if (!validateConfirmationEmail) {
								setValidateConfirmationEmail(true);
							}
							update('confirmationEmail', e.target.value);
							const confirmed = e.target.value === emailState.value;
							setEmailConfirmed(confirmed);
							updateEmail(emailState.value as string, { confirmed });
						}}
						onPaste={(e) => e.preventDefault()}
					/>
					<HelperTextWithIcon hasError={emailInputsAreInvalid()} helperText={renderErrorMessage()} />
				</>
			)}
		</>
	);
};

EmailInput.displayName = 'EmailInput';
